import React, { useEffect, useState } from 'react';
import { PageTracker } from '../../components/PageTracker.tsx';
import { 
  Settings, 
  Eye, 
  EyeOff, 
  ChevronDown, 
  ChevronRight, 
  RotateCcw,
  Save,
  Palette,
  Layout,
  Monitor
} from 'lucide-react';
import { useMenuConfig } from '../../hooks/useMenuConfig.ts';

function MenuCustomization() {
  const {
    config,
    visibleMenuItems,
    toggleItemVisibility,
    savePreferences,
    resetToDefaults,
    userPreferences
  } = useMenuConfig();

  const [expandedSections, setExpandedSections] = useState<string[]>(['menuItems']);
  const [hasChanges, setHasChanges] = useState(false);
  const [localPreferences, setLocalPreferences] = useState(userPreferences);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionKey)
        ? prev.filter(key => key !== sectionKey)
        : [...prev, sectionKey]
    );
  };

  const handlePreferenceChange = (key: string, value: any) => {
    const newPreferences = { ...localPreferences, [key]: value };
    setLocalPreferences(newPreferences);
    setHasChanges(true);
    console.log(`Preference changed: ${key} to ${value}`, newPreferences);
    
    // Apply theme immediately if it's a theme change
    if (key === 'theme') {
      applyTheme(value);
    }
  };

  useEffect(() => {
    console.log('Local preferences updated:', localPreferences);
    
    // Apply theme changes immediately (live preview)
    applyTheme(localPreferences.theme);
}, [localPreferences]);

  // Helper function to apply theme
  const applyTheme = (theme: string) => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else if (theme === 'system') {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  };

  const handleSavePreferences = () => {
    savePreferences(localPreferences);
    setHasChanges(false);
    
    // Update localStorage to sync with Layout component
    if (localPreferences.theme === 'dark') {
      localStorage.setItem('darkMode', JSON.stringify(true));
    } else if (localPreferences.theme === 'light') {
      localStorage.setItem('darkMode', JSON.stringify(false));
    } else if (localPreferences.theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      localStorage.setItem('darkMode', JSON.stringify(prefersDark));
    }
  };

  const handleReset = () => {
    resetToDefaults();
    setLocalPreferences(userPreferences);
    setHasChanges(false);
  };

  const renderMenuItem = (key: string, item: any, level: number = 0, parentPath: string = '') => {
    const itemPath = parentPath ? `${parentPath}.${key}` : key;
    const isHidden = userPreferences.hiddenItems.includes(itemPath);
    const hasChildren = item.children && Object.keys(item.children).length > 0;
    const isExpanded = expandedSections.includes(itemPath);

    return (
      <div key={itemPath} className={`${level > 0 ? 'ml-6' : ''}`}>
        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg mb-2">
          <div className="flex items-center gap-3">
            {hasChildren && (
              <button
                onClick={() => toggleSection(itemPath)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            )}
            {!hasChildren && <div className="w-6" />}
            
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {item.label}
              </span>
              {item.path && (
                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                  {item.path}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                toggleItemVisibility(itemPath);
                setHasChanges(true);
              }}
              className={`p-1 rounded transition-colors ${
                isHidden
                  ? 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                  : 'text-green-600 hover:text-green-700 dark:text-green-400'
              }`}
              title={isHidden ? 'Show item' : 'Hide item'}
            >
              {isHidden ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="ml-4 space-y-1">
            {Object.entries(item.children).map(([childKey, childItem]) =>
              renderMenuItem(childKey, childItem, level + 1, itemPath)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <PageTracker pageTitle="Menu Customization" category="settings" />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-3">
          <Settings className="h-8 w-8 text-blue-600" />
          Menu Customization
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Customize which items appear in your navigation menu and configure display preferences
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-3">
          <button
            onClick={handleSavePreferences}
            disabled={!hasChanges}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              hasChanges
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
            }`}
          >
            <Save className="h-4 w-4" />
            Save Changes
          </button>
          
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Defaults
          </button>
        </div>

        {hasChanges && (
          <div className="text-sm text-amber-600 dark:text-amber-400">
            You have unsaved changes
          </div>
        )}
      </div>

      {/* Display Preferences */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Display Preferences
          </h2>
          <button
            onClick={() => toggleSection('preferences')}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            {expandedSections.includes('preferences') ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </button>
        </div>

        {expandedSections.includes('preferences') && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900 dark:text-white">Theme</h3>
              <select
                value={localPreferences.theme}
                onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="system">System</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>

            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={localPreferences.compactMode}
                  onChange={(e) => handlePreferenceChange('compactMode', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Compact Mode</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={localPreferences.showIcons}
                  onChange={(e) => handlePreferenceChange('showIcons', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Show Icons</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={localPreferences.groupSimilarItems}
                  onChange={(e) => handlePreferenceChange('groupSimilarItems', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Group Similar Items</span>
              </label>
            </div>

            <div className="space-y-3">
              <h3 className="font-medium text-gray-900 dark:text-white">Statistics</h3>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <div>Total Items: {Object.keys(config.menuConfig).length}</div>
                <div>Visible Items: {Object.keys(visibleMenuItems).length}</div>
                <div>Hidden Items: {userPreferences.hiddenItems.length}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Menu Items Configuration */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Layout className="h-5 w-5" />
            Menu Items ({Object.keys(config.menuConfig).length} total)
          </h2>
          <button
            onClick={() => toggleSection('menuItems')}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            {expandedSections.includes('menuItems') ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </button>
        </div>

        {expandedSections.includes('menuItems') && (
          <div className="space-y-2">
            {Object.entries(config.menuConfig).map(([key, item]) =>
              renderMenuItem(key, item)
            )}
          </div>
        )}
      </div>

      {/* Current Configuration Preview */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Monitor className="h-5 w-5" />
          Current Configuration Preview
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Visible Sections:</h4>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              {Object.entries(visibleMenuItems).map(([key, item]) => (
                <li key={key} className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  {item.label}
                  {item.children && (
                    <span className="text-xs">({Object.keys(item.children).length} items)</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Hidden Items:</h4>
            {userPreferences.hiddenItems.length > 0 ? (
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                {userPreferences.hiddenItems.map(item => (
                  <li key={item} className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    {item.replace(/\./g, ' → ')}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 italic">No hidden items</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MenuCustomization;
